import { useCallback, useEffect, useRef, useState } from "react";
import { ChatMessageType } from "./messageTypes";
import { isSystemMessage } from "./verificationMessages";
import {
  isChatTranslationEnabled,
  toTranslateLangCode,
  translateChatText,
} from "./chatTranslate";

/**
 * Only the host's text messages are translated. Media captions and system
 * messages stay as sent — the banners match system text in English.
 */
export const isTranslatableMessage = (msg) =>
  Boolean(msg) &&
  !msg.isMine &&
  !isSystemMessage(msg) &&
  String(msg.messageType || ChatMessageType.TEXT).toLowerCase() ===
    ChatMessageType.TEXT &&
  Boolean(String(msg.text || "").trim());

const keyOf = (lang, id) => `${lang}:${id}`;

/**
 * Translates the host's messages into the guest's app language as they
 * arrive, once the chat guest id (the endpoint's auth) is known. The original
 * renders first and the translation replaces it when ready; "show original"
 * is a per-message toggle.
 */
const useChatTranslations = (messages, appLang, guestId) => {
  const targetLang = toTranslateLangCode(appLang);
  // Keyed by language + message id, so a language switch never shows stale text
  const [translations, setTranslations] = useState({});
  const [originalIds, setOriginalIds] = useState(() => new Set());
  const requestedRef = useRef(new Set());

  useEffect(() => {
    if (!targetLang || !guestId || !isChatTranslationEnabled()) return;
    messages.forEach((msg) => {
      if (!isTranslatableMessage(msg)) return;
      const key = keyOf(targetLang, msg.id);
      if (requestedRef.current.has(key)) return;
      requestedRef.current.add(key);
      translateChatText(msg.text, targetLang, guestId).then((translated) => {
        if (!translated) return;
        setTranslations((prev) => ({ ...prev, [key]: translated }));
      });
    });
  }, [messages, targetLang, guestId]);

  const getTranslation = useCallback(
    (msg) =>
      targetLang && msg?.id != null
        ? translations[keyOf(targetLang, msg.id)] || null
        : null,
    [translations, targetLang],
  );

  const isShowingOriginal = useCallback(
    (id) => originalIds.has(id),
    [originalIds],
  );

  const toggleOriginal = useCallback((id) => {
    setOriginalIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return { translations, getTranslation, isShowingOriginal, toggleOriginal };
};

export default useChatTranslations;
